import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, ArrowRight } from "lucide-react";
import BrandLogo from "../components/BrandLogo";
import BrandAttribution from "../components/BrandAttribution";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api, { formatApiError } from "../lib/api";

/**
 * Step 1 of OTP-based password reset.  The backend silently emails a
 * 6-digit code when the email exists (no enumeration leak), so we
 * always advance to /reset-password regardless of whether the address
 * is on file.  The toast wording is intentionally generic for the
 * same reason.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [devOtp, setDevOtp] = useState("");
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      // mock_otp is only surfaced when EMAIL_PROVIDER=mock — production
      // email is live (Brevo) and this field is always absent.
      if (data?.mock_otp) setDevOtp(data.mock_otp);
      if (data?.throttled) {
        toast.info("A code was sent recently — check your inbox.");
      } else {
        toast.success("If the email is on file, a 6-digit code is on the way.");
      }
      // Pass email through query so the reset screen pre-fills it.
      setTimeout(() => nav(`/reset-password?email=${encodeURIComponent(email.trim())}`), 600);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[#f6f8fb]">
      <div className="w-full max-w-md rounded-xl bg-white border border-slate-200 p-8 shadow-sm">
        <BrandLogo />
        <h1 className="mt-8 text-3xl font-bold tracking-tighter text-[#0A192F]">Forgot password?</h1>
        <p className="mt-2 text-sm text-slate-500">
          Enter your email and we&apos;ll send a 6-digit code to reset it.
        </p>
        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="forgot-form">
          <div>
            <Label className="text-xs font-semibold">Email</Label>
            <div className="mt-2 relative">
              <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 pl-9"
                placeholder="you@school.org"
                data-testid="forgot-email-input"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={submitting || !email.trim()}
            className="w-full h-12 bg-[#0F4C81] hover:bg-[#0A3860] gap-2"
            data-testid="forgot-submit-button"
          >
            {submitting ? "Sending…" : "Send 6-digit code"}
            {!submitting && <ArrowRight className="h-4 w-4" />}
          </Button>
        </form>
        {devOtp && (
          <div className="mt-6 p-4 rounded-md bg-[#0A192F] text-slate-200 text-xs">
            <p className="uppercase tracking-[0.18em] text-[#D4AF37] mb-1">DEV MODE — MOCK OTP</p>
            <p className="font-mono text-lg tracking-[0.32em] text-[#D4AF37]" data-testid="mock-otp">{devOtp}</p>
          </div>
        )}
        <p className="mt-6 text-sm text-slate-500">
          Remembered it?{" "}
          <Link to="/login" className="text-[#0F4C81] font-semibold hover:underline" data-testid="back-to-login-link">
            Sign in
          </Link>
        </p>
        <BrandAttribution className="mt-8" />
      </div>
    </div>
  );
}
